import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { getService, services } from "@/content/services";
import { company } from "@/content/site";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = slug ? getService(slug) : undefined;

  if (!service) return <NotFound />;

  const Icon = service.icon;
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      <Seo title={`${service.title} | ${company.name}`} description={service.summary} />

      {/* CHEVRON BANNER HEADER (Quality-style) */}
      <section className="bg-brand-50">
        <div className="mx-auto max-w-[1200px] px-6 py-8">
          <Link to="/services" className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>

          <div className="relative mt-4 overflow-hidden rounded-xl bg-brand-900 text-white">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 12px)" }}
            />
            {/* Chevron photo panel (desktop) */}
            {service.image && (
              <div
                className="absolute inset-y-0 right-0 hidden w-[46%] lg:block"
                style={{ clipPath: "polygon(22% 0, 100% 0, 100% 100%, 0% 100%)" }}
              >
                <img src={service.image} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-900 via-transparent to-transparent" />
              </div>
            )}
            <div className="relative max-w-xl p-8 lg:p-12">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-white">
                <Icon className="h-6 w-6" />
              </div>
              <h1 className="mb-3 text-3xl font-bold lg:text-4xl">{service.title}</h1>
              <p className="text-lg text-brand-200">{service.summary}</p>
            </div>
          </div>
        </div>
      </section>

      {/* BODY + HIGHLIGHTS */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-6 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            <div className="space-y-4 text-lg text-brand-600">
              {service.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "mt-8 w-full justify-center sm:w-auto")}>
              Get a quote <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-xl border border-brand-100 bg-brand-50 p-6">
              <div className="mb-4 text-sm font-semibold uppercase tracking-wider text-brand-400">
                What's included
              </div>
              <ul className="space-y-3">
                {service.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-brand-700">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent-500" /> {h}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="bg-brand-50 py-16 lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <h2 className="mb-8 text-2xl font-bold lg:text-3xl">Other services</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((s) => {
              const RIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group flex h-full flex-col rounded-lg border border-brand-100 bg-white p-6 transition-all hover:border-accent-500 hover:shadow-md"
                >
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-900 text-white transition-colors group-hover:bg-accent-500">
                    <RIcon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-2 text-lg">{s.title}</h3>
                  <p className="mb-4 text-sm text-brand-500">{s.summary}</p>
                  <span className="mt-auto inline-flex items-center text-sm font-semibold text-accent-500">
                    Learn more <ChevronRight className="ml-0.5 h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
